import { Component, OnInit, Inject } from '@angular/core';
import { IncidentCauseModel } from '../../incident-cause.model';
import { IncidentCauseService } from '../../incident-cause.service';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { UtilsService } from 'src/app/shared/utils.service';
import { ActionModel } from 'src/app/actions/action.model';
import { ActionsService } from 'src/app/actions/actions.service';
import { NetworkedUsersSelectorComponent } from 'src/app/shared/networked-users-selector/networked-users-selector.component';
import { FileUploaderService } from 'src/app/shared/file-uploader.service';
import { merge } from 'rxjs';
import { FilesRemoveComponent } from 'src/app/files/files-remove/files-remove.component';
import { ApiRequestService } from '../../../shared/api-request.service';
import moment from 'moment/moment';

@Component({
  selector: 'app-incidents-causes-edit',
  templateUrl: './incidents-causes-edit.component.html',
  styleUrls: ['./incidents-causes-edit.component.scss']
})
export class IncidentsCausesEditComponent implements OnInit {
  actionFiles: { [id: number]: FileList } = {};
  inProgress = false;

  selectedFiles: File[][] = [];

  priorities: string[] = ['Low', 'Medium', 'High', 'Critical'];

  types = this.utils.getActionTypes();

  constructor(
    private incidentCauseService: IncidentCauseService,
    @Inject(MAT_DIALOG_DATA) public incidentCause: IncidentCauseModel,
    private utils: UtilsService,
    private dialogRef: MatDialogRef<IncidentsCausesEditComponent>,
    private actionService: ActionsService,
    private fileUploaderService: FileUploaderService,
    private dialog: MatDialog,
    private api: ApiRequestService
  ) {}

  ngOnInit() {
    if (!this.incidentCause.id) {
      const defaultAction: Partial<ActionModel> = {
        type: 'Corrective Action',
        name: '',
        description: '',
        priority: null,
        due: null,
        completed: false,
        user_ids: [],
        files: []
      };

      return (this.incidentCause.actions = [defaultAction as ActionModel]);
    }
    this.incidentCauseService
      .getActions(this.incidentCause.id)
      .subscribe((response) => {
        this.incidentCause.actions = response.data;
      });
  }

  invalidActions() {
    // Incident causes must have actions.
    if (!this.incidentCause.actions || !this.incidentCause.actions.length) {
      return true;
    }

    // All actions must have assigned users.
    for (let i = 0; i < this.incidentCause.actions.length; i++) {
      if (
        !this.incidentCause.actions[i].user_ids ||
        !this.incidentCause.actions[i].user_ids.length
      ) {
        return true;
      }
    }

    return false;
  }

  onSelectUsers(index: number, action: ActionModel) {
    this.utils.showComponentDialog(
      NetworkedUsersSelectorComponent,
      {
        multiple: true,
        selected: action.user_ids,
        visitors_from_all_sites: true
      }, {
        width: '1200px'
      },
      (results) => {
        if (typeof results !== 'undefined') {
          this.incidentCause.actions[index].user_ids = results;
        }
      }
    );
  }

  onRemoveAction(index: number, action: ActionModel) {
    this.utils.showModal(
      'Remove Corrective Action',
      'Are you sure you want to remove the corrective action?',
      () => {
        this.removeAction(index, action);
    });
  }

  removeAction(index: number, action: ActionModel) {
    if ( action.id ) {
      this.actionService.remove([action.id]).subscribe(() => {
        this.utils.showToast('The corrective action was removed.');
      });
    }
    this.incidentCause.actions.splice(index, 1);
  }

  onAddAction() {
    const defaultAction: Partial<ActionModel> = {
      type: 'Corrective Action',
      name: '',
      description: '',
      priority: null,
      due: null,
      completed: false,
      user_ids: [],
      files: []
    };

    this.incidentCause.actions.push(defaultAction as ActionModel);
  }

  onCanSubmit() {
    // All causes should have actions.
    if (
      typeof this.incidentCause.actions === 'undefined' ||
      this.incidentCause.actions.length === 0
    ) {
      return false;
    }
    // All actions must be assigned to users.
    let canSubmit = true;
    this.incidentCause.actions.forEach((action) => {
      if (
        typeof action.user_ids === 'undefined' ||
        action.user_ids.length === 0
      ) {
        canSubmit = false;
      }
    });
    return canSubmit;
  }

  onSubmit() {
    if (!this.onCanSubmit()) {
      this.utils.showModal(
        'Validation Failed',
        'Please assign all corrective actions to users by clicking the <b>person</b> icon next to each corrective action.'
      );
      return;
    }

    if (this.inProgress) {
      return;
    }

    this.inProgress = true;

    this.incidentCause.id ? this.update() : this.create();
  }

  /**
   * Updates the current incident cause by calling the incidentCauseService and
   * uploads any selected files after a successful update. Displays appropriate
   * toast messages to indicate the success or failure of each operation.
   * Additionally, reinitializes the component state upon successful update and file upload.
   *
   * @return {void} This method does not return a value.
   */
  update(): void {
    this.incidentCauseService.update(this.incidentCause).subscribe(
      (response): void => {
        this.utils.showToast('Incident cause updated.');
        this.ngOnInit();
        this.inProgress = false;
      },
      (_error: any): void => {
        this.inProgress = false;
      }
    );
  }

  /**
   * Creates a new incident cause and handles related file uploads.
   * Displays appropriate toast notifications based on the operation's success or failure.
   *
   * @return {void} No return value.
   */
  create(): void {
    this.incidentCauseService.create(this.incidentCause).subscribe(
      (response): void => {
        this.utils.showToast('Incident cause created.');

        this.uploadSelectedFiles(response.data.actions)
          .then((uploadedAllFiles: boolean): void => {
            this.utils.showToast(uploadedAllFiles ? 'All files uploaded.' : 'Some files failed to upload.');
            this.dialogRef.close(true);
            this.inProgress = false;
          });
      },
      (_error: any): void => {
        this.inProgress = false;
      }
    );
  }

  /**
   * Handles the selection of files from an input and stores them in the selectedFiles array at the specified index.
   *
   * @param {number} index - The index in the selectedFiles array where the files should be stored.
   * @param {FileList} fileList - The list of files selected by the user.
   * @return {void} No return value.
   */
  onSelectFiles(index: number, fileList: FileList): void {
    const files: File[] = Array.from(fileList);
    if ( files && files.length > 0 ) {
      if (!this.selectedFiles[index]) {
        this.selectedFiles[index] = [];
      }
      this.selectedFiles[index].push(...files);
    }
  }

  /**
   * Uploads the selected files for the given actions by making an API request for each action.
   *
   * @param {ActionModel[]} actions - The list of actions associated with the files to be uploaded.
   * @return {Promise<boolean>} A promise that resolves to a boolean indicating whether all files were uploaded successfully.
   */
  private uploadSelectedFiles(actions: ActionModel[]): Promise<boolean> {
    return new Promise((resolve) => {
      let uploadedAllFiles: boolean = true;
      if (!this.selectedFiles || this.selectedFiles.length === 0) {
        resolve(false);
        return;
      }

      const uploadPromises: Promise<any>[] = [];
      const filesByGroup: Record<number, File[]> = {};

      actions.forEach((action) => {
        const groupId = action.group_id!;
        if (!(groupId in filesByGroup)) {
          filesByGroup[groupId] = this.selectedFiles[groupId] || [];
        }
      });

      // Upload files for every action in that group
      actions.forEach((action) => {
        const groupId = action.group_id!;
        const files = filesByGroup[groupId];
        if (!files || files.length === 0) {
          return;
        }

        uploadPromises.push(
          this.api.makeUploadRequest(`v2/file-manager/action/${action.id}`, files)
            .catch(() => {
              uploadedAllFiles = false;
            })
        );
      });

      Promise.all(uploadPromises)
        .then(() => resolve(uploadedAllFiles))
        .catch(() => resolve(false))
        .finally(() => { this.selectedFiles = []; });
    });
  }

  onDelete() {
    this.utils.showModal(
      'Remove Incident',
      'Are you sure you want to remove this incident?',
      () => {
        this.incidentCauseService
          .remove([this.incidentCause.id])
          .subscribe(() => {
            this.utils.showToast('Removed Cause.');
            this.dialogRef.close(true);
          });
      }
    );
  }

  onRemoveFiles(index: number) {
    const dialogRef = this.dialog.open(FilesRemoveComponent, {
      data: this.incidentCause.actions[index].files,
      width: '700px'
    });
    dialogRef.afterClosed().subscribe((response) => {
      this.incidentCause.actions[index].files = response;
    });
  }
}
